import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import POSTS_STATUS from '../../constants/status';
import Filters from '../../components/entries/Filters';
import type DraftInterface from '../../interfaces/Draft';
import DraftItem from '../entries/DraftItem';
import ListingIcon from '../../icons/ListingIcon';
import { getPosts } from '../../api/posts';
import { PAGE_SIZE } from '../../constants/pagination';
import { HOME_ERRORS } from '../../constants/errorMessages';
import { openSnackbar } from '../../context/reducers/generalSnackbar';
import PublicationSkeleton from '../home/PublicationSkeleton';
import { type RootState } from '../../context/store';
import DraftPlaceholderIcon from '../../icons/DraftPlaceholderIcon';
import { checkIfSomeFilterIsSelected } from '../../utils/entries';
import NotFoundIcon from '../../icons/NotFoundIcon';
import { type AuthorItem } from '../../interfaces/AuthorDetails';
import customDebounce from '../../utils/debounce';
import { setDraftPostAdded, setDraftPostUpdated } from '../../context/reducers/entries';
import { setDraftsQuantity } from '../../context/reducers/presentations';
import {
  setPresentationsDrafts,
  setPresentationsDraftsCurrentPage,
  setPresentationsDraftsIsLoadingMoreData,
  setPresentationsDraftsCanLoadMoreData,
  setPresentationsDraftsResetValues,
} from '../../context/reducers/presentationsDrafts';
import FiltersButton from '../globals/FiltersButton';
import TitleWithFiltersButton from '../globals/TitleWithFiltersButton';
import FiltersDrawer from '../entries/FiltersDrawer';
import SearchBar from '../entries/filtersDrawer/SearchBar';
import { FILE_TYPE } from '../../constants/globals';

export default function DraftsList(): JSX.Element {
  const drafts = useSelector((state: RootState) => state.presentationsDrafts.drafts);
  const draftsCurrentPage = useSelector(
    (state: RootState) => state.presentationsDrafts.currentPage,
  );
  const draftsIsLoadingMoreData = useSelector(
    (state: RootState) => state.presentationsDrafts.isLoadingMoreData,
  );
  const draftsCanLoadMoreData = useSelector(
    (state: RootState) => state.presentationsDrafts.canLoadMoreData,
  );
  const draftPostAdded = useSelector((state: RootState) => state.entries.draftPostAdded);
  const draftPostUpdated = useSelector((state: RootState) => state.entries.draftPostUpdated);
  const draftsQuantity = useSelector((state: RootState) => state.presentations.draftsQuantity);
  const isDrawerOpen = useSelector((state: RootState) => state.presentationsDrafts.isDrawerOpen);
  const [searchParams, setSearchParams] = useSearchParams();
  const [paramType, setParamType] = useState(POSTS_STATUS.DRAFT);
  const [selectAll, setSelectAll] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [resource, setResource] = useState<string | null>(null);
  const [selectedAuthors, setSelectedAuthors] = useState<any[]>([]);
  const [categoriesIDs, setCategoriesIDs] = useState<string[]>([]);
  const isMounted = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getAuthorsIDs = () => {
    return selectedAuthors.map((item: AuthorItem) => item.authorId);
  };

  const getAudioParam = () => {
    return resource == null ? undefined : resource === 'AUDIO';
  };

  useEffect(() => {
    setParamType(searchParams.get('type')?.toUpperCase() ?? POSTS_STATUS.DRAFT);
  }, [searchParams]);

  useEffect(() => {
    setSearchInput('');
    setResource(null);
    setSelectedAuthors([]);
    setCategoriesIDs([]);
  }, [paramType]);

  const callApiToGetDrafts = (value: string, wantToCancel: boolean, cleanSearch = false): void => {
    const newCurrentPage =
      draftsCurrentPage > 0
        ? draftsCanLoadMoreData
          ? draftsCurrentPage
          : draftsCurrentPage + 1
        : 0;
    const auxValue = value.trim()?.length >= 2 ? value?.trim() : '';
    if (
      draftPostAdded ||
      draftPostUpdated ||
      auxValue?.length > 0 ||
      cleanSearch ||
      categoriesIDs?.length > 0 ||
      selectedAuthors?.length > 0 ||
      getAudioParam() !== undefined
    ) {
      getPosts(
        auxValue,
        [POSTS_STATUS.DRAFT],
        PAGE_SIZE,
        0,
        'lastModifiedDate,DESC',
        categoriesIDs,
        getAuthorsIDs(),
        getAudioParam(),
        FILE_TYPE,
        wantToCancel,
      )
        .then((res) => {
          isMounted.current = true;
          dispatch(
            setPresentationsDrafts({
              drafts: res.content,
            }),
          );
          dispatch(
            setDraftsQuantity({
              draftsQuantity: res.totalElements,
            }),
          );
          if (!res.last) {
            dispatch(
              setPresentationsDraftsCurrentPage({
                currentPage: 1,
              }),
            );
            dispatch(
              setPresentationsDraftsCanLoadMoreData({
                canLoadMoreData: true,
              }),
            );
          } else {
            dispatch(
              setPresentationsDraftsCurrentPage({
                currentPage: 0,
              }),
            );
            dispatch(
              setPresentationsDraftsCanLoadMoreData({
                canLoadMoreData: false,
              }),
            );
          }
          dispatch(
            setDraftPostAdded({
              draftPostAdded: false,
            }),
          );
          dispatch(
            setDraftPostUpdated({
              draftPostUpdated: false,
            }),
          );
        })
        .catch((error: unknown) => {
          let errorStatus;
          if (error instanceof AxiosError) {
            errorStatus = error?.response?.status;
          }
          if (!axios.isCancel(error) && errorStatus !== 401) {
            dispatch(
              openSnackbar({
                type: 'error',
                message: HOME_ERRORS.DRAFTS_LIST,
              }),
            );
          }
        });
    } else {
      getPosts(
        auxValue,
        [POSTS_STATUS.DRAFT],
        PAGE_SIZE,
        newCurrentPage,
        'lastModifiedDate,DESC',
        categoriesIDs,
        getAuthorsIDs(),
        getAudioParam(),
        FILE_TYPE,
        wantToCancel,
      )
        .then((res) => {
          isMounted.current = true;
          if (drafts === null || newCurrentPage === 0) {
            dispatch(
              setPresentationsDrafts({
                drafts: res.content,
              }),
            );
          } else {
            dispatch(
              setPresentationsDrafts({
                drafts: [...drafts, ...res.content],
              }),
            );
          }
          dispatch(
            setDraftsQuantity({
              draftsQuantity: res.totalElements,
            }),
          );
          if (!res.last) {
            dispatch(
              setPresentationsDraftsCurrentPage({
                currentPage: newCurrentPage + 1,
              }),
            );
            dispatch(
              setPresentationsDraftsCanLoadMoreData({
                canLoadMoreData: true,
              }),
            );
          } else {
            dispatch(
              setPresentationsDraftsCanLoadMoreData({
                canLoadMoreData: false,
              }),
            );
          }
        })
        .catch((error: unknown) => {
          let errorStatus;
          if (error instanceof AxiosError) {
            errorStatus = error?.response?.status;
          }
          if (!axios.isCancel(error) && errorStatus !== 401) {
            dispatch(
              openSnackbar({
                type: 'error',
                message: HOME_ERRORS.DRAFTS_LIST,
              }),
            );
          }
        });
    }
  };

  useEffect(() => {
    callApiToGetDrafts(searchInput, false);
  }, [categoriesIDs, selectedAuthors, resource]);

  const loadMoreData = async () => {};

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
      const margin = 400;
      if (
        scrollTop + clientHeight + margin >= scrollHeight &&
        paramType.toUpperCase() === POSTS_STATUS.DRAFT
      ) {
        loadMoreData();
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [draftsCurrentPage, draftsCanLoadMoreData, draftsIsLoadingMoreData, paramType, searchInput]);

  const handleNewPost = () => {
    navigate('/new-presentation', {
      state: {
        prevRouteName: location.pathname,
      },
    });
  };

  const handleInputChange = (value: string): void => {
    setSearchInput(value);
    const debounceSearch = customDebounce(callApiToGetDrafts, 500);
    if (value?.length >= 2) {
      debounceSearch(value, true);
    } else {
      debounceSearch(value, true, true);
    }
  };

  return (
    <Container>
      {paramType.toUpperCase() === POSTS_STATUS.DRAFT && (
        <>
          <Filters
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            resource={resource}
            setResource={setResource}
            authorsIDs={selectedAuthors}
            setAuthorsIDs={setSelectedAuthors}
            categoriesIDs={categoriesIDs}
            setCategoriesIDs={setCategoriesIDs}
            handleInputChange={handleInputChange}
          />
          <FiltersDrawer
            isOpen={isDrawerOpen}
            type={POSTS_STATUS.DRAFT}
            resource={resource}
            setResource={setResource}
            authorsIDs={selectedAuthors}
            setAuthorsIDs={setSelectedAuthors}
            categoriesIDs={categoriesIDs}
            setCategoriesIDs={setCategoriesIDs}
            contentType='PRESENTATION'
          />
          <TitleWithFiltersButton
            title='Borradores'
            quantity={draftsQuantity}
            type={POSTS_STATUS.DRAFT}
            contentType='PRESENTATION'
          />
          <SearchBar title={'Borradores'} handleInputChange={handleInputChange} />
          {drafts === null ? (
            <SkeletonsContainer>
              <PublicationSkeleton />
              <PublicationSkeleton />
              <PublicationSkeleton />
              <PublicationSkeleton />
              <PublicationSkeleton />
            </SkeletonsContainer>
          ) : drafts?.length === 0 ? (
            <MessageContainer>
              {checkIfSomeFilterIsSelected(
                resource,
                selectedAuthors,
                categoriesIDs,
                searchInput,
              ) ? (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <NotFoundIcon />
                  </div>
                  <Message style={{ maxWidth: '280px' }}>
                    ¡Lo sentimos! No encontramos ningún PPT relacionado a tu búsqueda, prueba con
                    otra palabra.
                  </Message>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <DraftPlaceholderIcon width={30} height={30} />
                  </div>
                  <Message>Aún no tienes PPTs.</Message>
                  <Message>¿Te encuentras inspirado?</Message>
                  <CustomButton className='bg-primary px-8 rounded-2xl' onClick={handleNewPost}>
                    <ButtonText>Empezar nuevo PPT</ButtonText>
                  </CustomButton>
                </>
              )}
            </MessageContainer>
          ) : (
            <>
              {drafts?.map((draft, index) => (
                <DraftItem key={`${draft.id}`} {...draft} drafts={drafts} />
              ))}
              {draftsCanLoadMoreData && (
                <SkeletonsContainer>
                  <PublicationSkeleton />
                  <PublicationSkeleton />
                  <PublicationSkeleton />
                  <PublicationSkeleton />
                  <PublicationSkeleton />
                </SkeletonsContainer>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  flex: 1;

  @media screen and (max-width: 860px) {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding-top: 100px;
  height: calc(100vh - 420px);
`;

interface MessageI {
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const Message = styled.p<MessageI>`
  font-family: ${(props) => props.theme.fonts.medium};
  color: ${(props) => props.theme.colors.black};
  font-size: 16px;
  text-align: center;
  margin-bottom: 3px;
`;

const CustomButton = styled(Button)`
  background-color: ${(props) => props.theme.colors.skyBlue};
  border-radius: 8px;
  margin-top: 20px;
`;

const ButtonText = styled.p`
  text-transform: none;
  font-family: ${(props) => props.theme.fonts.regular};
  color: white;
  font-size: 13px;
`;

const SkeletonsContainer = styled.div`
  padding-top: 15px;
  padding-left: 29px;
  padding-right: 20px;
  @media screen and (max-width: 860px) {
    padding: 15px 0px;
  }
`;
